/**
 * @module collectionRequestStatusStyles
 * @purpose Colour and icon lookup for the two-dimension collection-request status model (packageStatus/returnStatus, PRP §2 C2).
 * @depends lucide-react icons, PackageStatus/ReturnStatus types
 * @dependedOnBy PackageReturnStatusBadges, pages/case/case-collections-tab/request-status-styles.tsx
 *
 * @rules
 * - State: none -- pure lookup tables (rules-as-data)
 * - Errors: n/a
 */

import type { ReactNode } from 'react'
import { CheckCircle, Clock, PauseCircle, Package, RefreshCw, Send, XCircle } from 'lucide-react'
import type { PackageStatus, ReturnStatus } from '../../../types/collection-requests'

export const PACKAGE_STATUS_COLOURS: Record<PackageStatus, string> = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  pending: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  generating: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
  ready: 'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-400',
  sent: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
  stalled: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400',
  ingested: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400',
  cancelled: 'bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-400',
  expired: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500',
}

export const PACKAGE_STATUS_ICONS: Record<PackageStatus, ReactNode> = {
  draft: <Clock className="h-4 w-4" />,
  pending: <Clock className="h-4 w-4" />,
  generating: <RefreshCw className="h-4 w-4 animate-spin" />,
  ready: <Package className="h-4 w-4" />,
  sent: <Send className="h-4 w-4" />,
  stalled: <PauseCircle className="h-4 w-4" />,
  ingested: <CheckCircle className="h-4 w-4" />,
  failed: <XCircle className="h-4 w-4" />,
  cancelled: <XCircle className="h-4 w-4" />,
  expired: <XCircle className="h-4 w-4" />,
}

export const RETURN_STATUS_COLOURS: Record<Exclude<ReturnStatus, 'none'>, string> = {
  partially_returned: 'bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-400',
  fully_returned: 'bg-teal-100 text-teal-700 dark:bg-teal-950/50 dark:text-teal-400',
  completed: 'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-400',
}

export function packageStatusColourFor(status: PackageStatus): string {
  return PACKAGE_STATUS_COLOURS[status]
}

export function packageStatusIconFor(status: PackageStatus): ReactNode {
  return PACKAGE_STATUS_ICONS[status]
}

export function returnStatusColourFor(status: ReturnStatus): string | null {
  if (status === 'none') return null
  return RETURN_STATUS_COLOURS[status]
}
